import type { ParsedFfprobeOutput } from './ffprobeOutput';
import { TECHNICAL_IMAGE_DATA_MDF_ID, TECHNICAL_VIDEO_DATA_MDF_ID, TECHNICAL_AUDIO_DATA_MDF_ID } from '../mdf/mdfDefaults';
import type { FieldValue, TechnicalMetadata } from '../dto/fieldValue';
import type { MdfFieldEntity } from '../mdf/fieldDefinition';
import type { ExifTags} from '../dto/exif';
import { findExifLeafPropertyAsNumber, findExifLeafPropertyAsString, findExifLeafProperty } from '../dto/exif';
import omit from 'lodash/omit';

// These are shared by both audio and video media
export enum TechnicalAudioAndVideoDataField {
  CodecName = 'technical_media_codec_name',
  CodecTag = 'technical_media_codec_tag',
  AudioChannels = 'technical_media_audio_channels',
  AudioChannelsInStreams = 'technical_media_audio_channels_in_streams',
  AudioSampleRate = 'technical_media_audio_sample_rate',
  // Duration is expressed in milliseconds
  Duration = 'technical_media_duration',
  BitRate = 'technical_media_bitrate',
  ContainerFormat = 'technical_media_container_format',
  IsTruncated = 'technical_media_is_truncated',
  AudioBitDepth = 'technical_media_audio_bit_depth',
  CompanyName = 'technical_media_company_name',
  ProductName = 'technical_media_product_name',
}

export enum TechnicalVideoDataField {
  FrameRate = 'technical_video_frame_rate',
  Width = 'technical_video_width',
  Height = 'technical_video_height',
  FieldOrder = 'technical_video_field_order',
  ColorSpace = 'technical_video_color_space',
  PixelFormat = 'technical_video_pixel_format',
  Profile = 'technical_video_profile',
  Level = 'technical_video_level',
  AudioCodecName = 'technical_media_audio_codec_name',
  AudioBitRate = 'technical_media_audio_bitrate',
  PixelAspectRatio = 'technical_video_pixel_aspect_ratio',
  DisplayAspectRatio = 'technical_video_display_aspect_ratio',
  TimeBase = 'technical_video_time_base',
}

export enum TechnicalImageDataField {
  Width = 'technical_image_width',
  Height = 'technical_image_height',
  FileType = 'technical_image_file_type',
}

export enum ComputedTechnicalField {
  Width = 'technical_media_width',
  Height = 'technical_media_height',
  DisplayAspectRatio = 'technical_media_display_aspect_ratio',
}

export type TechnicalMetadataField =
  TechnicalAudioAndVideoDataField |
  TechnicalVideoDataField |
  TechnicalImageDataField |
  ComputedTechnicalField;

export function getTechnicalMetadataValue<T extends FieldValue>(
  metadata: TechnicalMetadata | undefined,
  field: TechnicalMetadataField
): T | null {
  if (field === ComputedTechnicalField.Height) {
    return (metadata?.formData?.[TechnicalVideoDataField.Height] ?? metadata?.formData?.[TechnicalImageDataField.Height]) as T ?? null;
  }
  if (field === ComputedTechnicalField.Width) {
    return (metadata?.formData?.[TechnicalVideoDataField.Width] ?? metadata?.formData?.[TechnicalImageDataField.Width]) as T ?? null;
  }
  return (metadata?.formData ?? {})[field] as T ?? null;
}

export function getTechnicalImageDataFieldId(fieldId: string) {
  // We allow users to add their own fields to the image form.
  // The fieldId is then matched against an exif tag, and therefore
  // fields with the same fieldId will always represent the same
  // field / exif tag. To index these as one field across tenants
  // in OpenSearch we generate the same field.id when the fieldId is the same.
  return `image_exif_${fieldId}`;
}

const ffprobeToFormFieldsMapping: Record<string, keyof ParsedFfprobeOutput> = {
  [TechnicalAudioAndVideoDataField.CodecName]: 'codecName',
  [TechnicalAudioAndVideoDataField.CodecTag]: 'codecTagString',
  [TechnicalAudioAndVideoDataField.Duration]: 'duration',
  [TechnicalAudioAndVideoDataField.AudioChannels]: 'audioChannels',
  [TechnicalAudioAndVideoDataField.AudioChannelsInStreams]: 'audioChannelsInStreams',
  [TechnicalAudioAndVideoDataField.AudioSampleRate]: 'sampleRate',
  [TechnicalAudioAndVideoDataField.BitRate]: 'bitRateMb',
  [TechnicalAudioAndVideoDataField.ContainerFormat]: 'containerFormat',
  [TechnicalAudioAndVideoDataField.AudioBitDepth]: 'audioBitDepth',
  [TechnicalAudioAndVideoDataField.CompanyName]: 'companyName',
  [TechnicalAudioAndVideoDataField.ProductName]: 'productName',
  [TechnicalVideoDataField.FrameRate]: 'frameRate',
  [TechnicalVideoDataField.Width]: 'width',
  [TechnicalVideoDataField.Height]: 'height',
  [TechnicalVideoDataField.FieldOrder]: 'fieldOrder',
  [TechnicalVideoDataField.ColorSpace]: 'colorSpace',
  [TechnicalVideoDataField.PixelFormat]: 'pixelFormat',
  [TechnicalVideoDataField.Profile]: 'profile',
  [TechnicalVideoDataField.Level]: 'level',
  [TechnicalVideoDataField.AudioCodecName]: 'audioCodecName',
  [TechnicalVideoDataField.AudioBitRate]: 'totalAudioBitrate',
  [TechnicalVideoDataField.PixelAspectRatio]: 'sampleAspectRatio',
  [TechnicalVideoDataField.DisplayAspectRatio]: 'displayAspectRatio',
  [TechnicalVideoDataField.TimeBase]: 'timeBase',
};

export const technicalFieldsToOmitForCloudLive = [TechnicalAudioAndVideoDataField.CodecTag, TechnicalAudioAndVideoDataField.Duration];
const ffprobeToFormFieldsMappingForLiveVideo = omit(ffprobeToFormFieldsMapping, technicalFieldsToOmitForCloudLive);

const videoFields = [
  TechnicalVideoDataField.FrameRate,
  TechnicalVideoDataField.Width,
  TechnicalVideoDataField.Height,
  TechnicalVideoDataField.FieldOrder,
  TechnicalVideoDataField.ColorSpace,
  TechnicalVideoDataField.PixelFormat,
  TechnicalVideoDataField.Profile,
  TechnicalVideoDataField.Level,
  TechnicalVideoDataField.PixelAspectRatio,
  TechnicalVideoDataField.DisplayAspectRatio,
  TechnicalVideoDataField.TimeBase,
];
const ffprobeToFormFieldsMappingForLiveAudio = omit(ffprobeToFormFieldsMapping, [...technicalFieldsToOmitForCloudLive, ...videoFields]);

function getValueFromFfprobeOutput(ffprobeOutput: ParsedFfprobeOutput, ffProbeField: keyof ParsedFfprobeOutput) {
  let val = ffprobeOutput[ffProbeField];
  if (ffProbeField === 'fieldOrder') {
    switch (val) {
      case 'tt':
      case 'bt': val = 'top first'; break;
      case 'bb':
      case 'tb': val = 'bottom first'; break;
    }
  }
  return val;
}


function getFormValuesFromFfprobeData(ffprobeOutput: ParsedFfprobeOutput, fieldMapping: Record<string, keyof ParsedFfprobeOutput>): Record<string, string | number | number[]> {
  const formData: Record<string, string | number | number[]> = {};
  Object.entries(fieldMapping).forEach(([formField, ffProbeField]) => {
    const val = getValueFromFfprobeOutput(ffprobeOutput, ffProbeField);
    if (val !== undefined) {
      formData[formField] = val;
    }
  });

  if (ffprobeOutput.rotation === 90 || ffprobeOutput.rotation === 270 || ffprobeOutput.rotation === -90 || ffprobeOutput.rotation === -270) {
    const width = formData[TechnicalVideoDataField.Width];
    formData[TechnicalVideoDataField.Width] = formData[TechnicalVideoDataField.Height];
    formData[TechnicalVideoDataField.Height] = width;
  }

  return formData;
}


/**
 * Creates the technical video metadata form for live video corresponding
 * to the given ffprobe data.
 */
export function getLiveVideoTechnicalMetadata(ffprobeOutput: ParsedFfprobeOutput): TechnicalMetadata {
  const formData = getFormValuesFromFfprobeData(ffprobeOutput, ffprobeToFormFieldsMappingForLiveVideo);
  // ffprobe for TS segments returns - "MPEG-TS (MPEG-2 Transport Stream)" as container format.
  // Hence only taking the first part.
  formData[TechnicalAudioAndVideoDataField.ContainerFormat] = (formData[TechnicalAudioAndVideoDataField.ContainerFormat] as string).trim().split(' ')[0];
  return {formId: TECHNICAL_VIDEO_DATA_MDF_ID, formData};
}


/**
 * Creates the technical video metadata form for live audio corresponding
 * to the given ffprobe data.
 */
export function getLiveAudioTechnicalMetadata(ffprobeOutput: ParsedFfprobeOutput): TechnicalMetadata {
  const formData = getFormValuesFromFfprobeData(ffprobeOutput, ffprobeToFormFieldsMappingForLiveAudio);
  // ffprobe for TS segments returns - "MPEG-TS (MPEG-2 Transport Stream)" as container format.
  // Hence only taking the first part.
  formData[TechnicalAudioAndVideoDataField.ContainerFormat] = (formData[TechnicalAudioAndVideoDataField.ContainerFormat] as string).trim().split(' ')[0];
  return {formId: TECHNICAL_AUDIO_DATA_MDF_ID, formData};
}


/**
 * Creates the technical video metadata form corresponding
 * to the given ffprobe data.
 */
export function getVideoTechnicalMetadata(ffprobeOutput: ParsedFfprobeOutput): TechnicalMetadata {
  const formData = getFormValuesFromFfprobeData(ffprobeOutput, ffprobeToFormFieldsMapping);
  return {formId: TECHNICAL_VIDEO_DATA_MDF_ID, formData};
}

/**
 * Creates the technical audio metadata form corresponding
 * to the given ffprobe data.
 */
export function getAudioTechnicalMetadata(ffprobeOutput: ParsedFfprobeOutput): TechnicalMetadata {
  const formData = getFormValuesFromFfprobeData(ffprobeOutput, ffprobeToFormFieldsMapping);
  return {formId: TECHNICAL_AUDIO_DATA_MDF_ID, formData};
}

/**
 * Creates the technical image metadata form corresponding
 * to the given exif data.
 */
export function getImageTechnicalMetadata(exifTags: ExifTags, imageFormFields: MdfFieldEntity[]): TechnicalMetadata {
  const formData: Record<string, string | number | boolean> = {
    [TechnicalImageDataField.Width]: findExifLeafPropertyAsNumber(exifTags, 'ImageWidth') ?? 0,
    [TechnicalImageDataField.Height]: findExifLeafPropertyAsNumber(exifTags, 'ImageHeight') ?? 0,
    [TechnicalImageDataField.FileType]: findExifLeafPropertyAsString(exifTags, 'FileType') ?? '',
  };

  // For the remaining user-defined fields we dynamically
  // match the fieldId against the exif tag
  for (const field of imageFormFields) {
    const value = findExifLeafProperty(exifTags, field.fieldId);
    if (value !== undefined) {
      formData[field.id] = value;
    }
  }

  const orientation = findExifLeafPropertyAsNumber(exifTags, 'Orientation') ?? 1;
  if (orientation === 6 || orientation === 8) {
    const width = formData[TechnicalImageDataField.Width];
    formData[TechnicalImageDataField.Width] = formData[TechnicalImageDataField.Height];
    formData[TechnicalImageDataField.Height] = width;
  }
  return {formId: TECHNICAL_IMAGE_DATA_MDF_ID, formData};
}
